// MoyRi screen help (the "?" at the top of MoyRi). Plain words; keep it in step with
// apps/mobile/src/olmps/SpectrumScreen.tsx (seat cards, ACTIVE SWAPS, SWAP page, settings).
import type { ScreenHelpContent } from '../../v10/ScreenHelp';

export const moyriHelp: ScreenHelpContent = {
  title: 'MoyRi',
  intro:
    "MoyRi shows who holds each of your five ORDER seats: the helpful deity, or a bad habit (a malady) that has taken the seat. When a malady holds a seat, MoyRi gives you a SWAP: a plan to trade the habit for the deity, one step at a time.",
  sections: [
    {
      heading: 'The five seats',
      body: ['Each card is one seat. The top word is the part of the ORDER cycle, and the name below it is whoever holds the seat right now.'],
      items: [
        { term: 'Orient', text: 'ATLAS. Where am I going?' },
        { term: 'Regulate', text: 'POROS. What do I need?' },
        { term: 'Direct', text: 'KYRS. How will I get there?' },
        { term: 'Execute', text: 'METIS. Do the thing.' },
        { term: 'Revise', text: 'CLIO. What changed?' },
      ],
    },
    {
      heading: 'Reading a card',
      body: [
        'The round picture shows the holder. If the deity holds the seat it shows the deity and "Seat +2.0". If a malady holds it, the card shows the malady in red and reads "SEV 2" (how firmly it holds, 1 to 4) and the score.',
        'The little bar runs from -4 (the malady) to +4 (the deity). The dot is your seat score.',
        'The last line, "Intake", says how entrenched the habit was when you took the health check: healthy, mild, established or entrenched. "not checked" in red means you have not checked that seat yet.',
      ],
    },
    {
      heading: 'Where the score comes from',
      body: [
        'Seats start at 0. Your score comes only from what you actually do over the last 30 days: each time something is due, you either use the deity\'s way (a deity mark) or the habit\'s way, or skip it (a malady mark). Above 0 the deity holds the seat. Below 0 the malady does. It flips at exactly 0.',
        'Until a seat has 8 days of marks it shows "—". The health check never sets the seat. It only tells you how entrenched the habit is.',
      ],
    },
    {
      heading: 'Tap a card',
      body: [
        'An unchecked seat starts the health check for that seat. A checked seat opens a profile card that turns over by itself.',
        'Deity profile: your ladder (Training, Unlocked or Acquired), your seat score, and the deity\'s principles for your level. Tap a principle to make it one of yours, then press "mark done today" each day you live it. These save as you tap. "Chat with" opens a chat with that deity.',
        'Malady profile: why it exists, its symptoms, and the SWAP laid out line by line. "Open the full SWAP" takes you to the full page.',
      ],
    },
    {
      heading: 'The health check',
      body: [
        'Under the cards, "Take the ORDER Health Check" (or "Check n remaining seats") asks quick questions about the last few weeks. Answer Never, Rarely, Sometimes or Usually. If a seat looks shaky, a few more questions narrow down which habit it is. It saves as you go. "Retake Health Check" at the bottom does all five seats again.',
      ],
    },
    {
      heading: 'Active SWAPs',
      body: [
        'Each card here is a seat a malady holds, with the malady on the left, the deity on the right, and your score between them. It also says how many good reps you made this week and how many slips. WATCHING or PAUSED shows whether MoyRi is watching that seat. Tap a card for the full SWAP page.',
        'The SWAP page has: DISSOLVE NOW (a first move, with a button that opens that deity\'s tool), why the habit exists, its symptoms, the deity\'s signs, the S.W.A.P. rows, KEEP IT GONE, the COST if the habit keeps winning, and HOW MOYRI WATCHES. Open "MORE" for the full profile and history.',
        'On the SWAP page, SEVERITY, SEAT, REPS / WK and your ladder are shown as tiles. For some seats you will see two buttons to log by hand: "Used" followed by the deity\'s name, or the malady\'s name followed by "won". Each tap is one extra mark and saves right away. Using the deity\'s tool at the right moment also counts as a rep.',
      ],
      items: [
        { term: 'S', text: 'Situation: what sets the habit off.' },
        { term: 'W', text: 'Want: the need it is really feeding.' },
        { term: 'A', text: 'Action: what you do.' },
        { term: 'P', text: 'Payoff: what you get out of it, and how the deity pays the same.' },
      ],
    },
    {
      heading: 'Resolved SWAPs and Settings',
      body: [
        '"Resolved SWAPs" lists seats a malady held at your health check that the deity has since taken back.',
        '"MoyRi Settings" lists the five seats. Re-check redoes one seat\'s check. SWAP ON means MoyRi watches that seat and sends you to its tool when the habit fires. SWAP OFF pauses it. The switch saves right away.',
      ],
    },
  ],
};
